import type { Availability, EvaSession, Registration, SessionStatus } from "./types";

export const weekdays = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

export function formatSessionDate(value: string) {
  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

export function registeredCount(sessionId: string, registrations: Registration[]) {
  return registrations.filter((entry) => entry.session_id === sessionId && entry.status === "registered").length;
}

export function visibleTakenSeats(session: EvaSession, registrations: Registration[]) {
  return session.source === "eva" && typeof session.eva_taken_seat_count === "number"
    ? session.eva_taken_seat_count
    : registeredCount(session.id, registrations);
}

export function sessionStatus(session: EvaSession, registrations: Registration[]): SessionStatus {
  const count = visibleTakenSeats(session, registrations);
  if (count >= session.capacity) return "full";
  if (session.capacity - count <= 2) return "almost-full";
  return "available";
}

export function isAvailabilityMatching(session: EvaSession, availability: Availability, sessions: EvaSession[] = []) {
  if (availability.session_id) {
    return availability.session_id === session.id;
  }

  if (sessions.length > 0 && findLegacyAvailabilitySessionId(availability, sessions) !== session.id) {
    return false;
  }

  return isAvailabilitySlotMatching(session, availability);
}

function findLegacyAvailabilitySessionId(availability: Availability, sessions: EvaSession[]) {
  const now = new Date();
  return [...sessions]
    .filter((session) => new Date(session.starts_at) > now)
    .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())
    .find((session) => isAvailabilitySlotMatching(session, availability))?.id;
}

function isAvailabilitySlotMatching(session: EvaSession, availability: Availability) {
  const start = new Date(session.starts_at);
  const end = new Date(start.getTime() + session.duration_minutes * 60_000);
  const day = start.getDay();
  const startMinutes = start.getHours() * 60 + start.getMinutes();
  let endMinutes = end.getHours() * 60 + end.getMinutes();
  const availabilityStart = timeToMinutes(availability.start_time);
  const availabilityEnd = timeToMinutes(availability.end_time);

  if (end.getDay() !== day || endMinutes <= startMinutes) {
    endMinutes += 24 * 60;
  }

  return availability.weekday === day && availabilityStart <= startMinutes && availabilityEnd >= endMinutes;
}

function timeToMinutes(value: string) {
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

export function uid(prefix: string) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}
